/**
 * Marquee edge fade sizing.
 *
 * The fade has to be at least as wide as the widest pill, or the pill being
 * clipped at the edge shows its top and bottom borders running off with no
 * rounded cap — an open capsule. A fixed px value cannot know how wide the
 * pills are: that depends on the merchant's labels, the font and the padding
 * settings. So it is measured.
 *
 * Capped at a share of the bar, because on a narrow screen a pill can be more
 * than half the width and fading it whole would leave nothing solid.
 */
const TRM_MAX_SHARE = 0.45;

class TrMarquee extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.tr-marquee__viewport');
    if (!this.viewport || !this.viewport.classList.contains('tr-marquee__viewport--fade')) return;

    this.measure = () => this.#apply();
    this.#apply();

    // Pill widths change with the viewport, with font loading, and when the
    // merchant edits a label, so re-measure on any reflow rather than only on
    // window resize.
    if ('ResizeObserver' in window) {
      this.observer = new ResizeObserver(this.measure);
      this.observer.observe(this.viewport);
    }

    // Web fonts land after first paint and change the text width.
    document.fonts?.ready.then(this.measure).catch(() => {});
  }

  #apply() {
    const pills = this.querySelectorAll('.tr-marquee__pill');
    if (!pills.length) return;

    let widest = 0;
    pills.forEach((pill) => {
      const w = pill.getBoundingClientRect().width;
      if (w > widest) widest = w;
    });
    if (!widest) return;

    const barWidth = this.viewport.getBoundingClientRect().width;
    if (!barWidth) return;

    // Read the gap so the fade clears the space between pills too.
    const gap = parseFloat(getComputedStyle(this).getPropertyValue('--trm-gap')) || 14;

    const needed = widest + gap;
    const ceiling = barWidth * TRM_MAX_SHARE;
    const fade = Math.min(needed, ceiling);

    this.viewport.style.setProperty('--trm-fade', `${Math.round(fade)}px`);
    // Exposed for debugging and for anyone checking whether the cap bit.
    this.dataset.trmFadeCapped = String(needed > ceiling);
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }
}

if (!customElements.get('tr-marquee')) customElements.define('tr-marquee', TrMarquee);
